import {
  Dispatch,
  MouseEvent,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

const SKIP_INTERVAL = 10;

interface UseCustomPlayerProps {
  playerRef: RefObject<HTMLVideoElement>;
  playing: boolean;
  setPlaying: Dispatch<SetStateAction<boolean>>;
  autoPlay?: boolean;
  isLive?: boolean;
}

export const useCustomPlayer = ({
  playerRef,
  playing,
  setPlaying,
  autoPlay,
  isLive,
}: UseCustomPlayerProps) => {
  const [started, setStarted] = useState(autoPlay ?? false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferedPercentage, setBufferedPercentage] = useState(0);
  const [volume, setVolume] = useState(1);

  const togglePlay = useCallback(
    (playing?: boolean) => {
      setStarted(true);
      if (playerRef.current) {
        setPlaying((p) => (playing ? playing : !p));
      }
    },
    [playerRef, setPlaying, setStarted],
  );

  useEffect(() => {
    const player = playerRef.current;
    if (player) {
      if (playing !== undefined) {
        // Set explicitly
        if (playing) {
          player.play().catch(() => {
            // If autoplay fails due to browser policy, reset player state so user has to click play
            setPlaying(false);
            setStarted(false);
          });
        } else {
          player.pause();
        }
      } else {
        // Toggle
        if (!player?.paused) {
          player.pause();
        } else {
          player.play();
        }
      }
    }
  }, [playerRef, playing, setPlaying]);

  const toggleMute = useCallback(() => {
    const player = playerRef.current;
    if (player) {
      player.muted = !player.muted;
      setMuted(player.muted);
    }
  }, [playerRef]);

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(event.target.value);
    setVolume(newVolume);
    if (playerRef.current) {
      playerRef.current.volume = newVolume;
      playerRef.current.muted = newVolume === 0;
    }
    setMuted(newVolume === 0);
  };

  const handleVolumeIncrease = useCallback(
    (value: number) => {
      setVolume((c) => {
        const newValue = c + value;
        const newVolume = newValue > 0.9 ? 1 : newValue < 0.1 ? 0 : newValue;
        if (playerRef.current) {
          playerRef.current.volume = newVolume;
          playerRef.current.muted = newVolume === 0;
        }
        setMuted(newVolume === 0);
        return newVolume;
      });
    },
    [playerRef],
  );

  const handleTimeUpdate = () => {
    const player = playerRef.current;
    if (player) {
      setCurrentTime(player.currentTime);
    }
  };

  const handleProgress = useCallback(() => {
    if (playerRef.current) {
      const buffered = playerRef.current.buffered;
      if (buffered.length > 0) {
        const bufferedEnd = buffered.end(buffered.length - 1);
        const duration = playerRef.current.duration;
        setBufferedPercentage((bufferedEnd / duration) * 100);
      }
    }
  }, [playerRef]);

  const handleLoadedMetadata = () => {
    const player = playerRef.current;
    if (player) {
      setDuration(player.duration);
    }
  };

  const handleSeek = (event: MouseEvent<HTMLDivElement>) => {
    const player = playerRef.current;
    if (!player) return;
    const target = event.currentTarget as HTMLDivElement;
    player.currentTime = Math.floor(
      (event.nativeEvent.offsetX / target.clientWidth) * player?.duration,
    );
    setCurrentTime(player.currentTime);
  };
  const handleScrub = (e: MouseEvent<HTMLDivElement>) => {
    if (e.buttons === 1) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));

      if (playerRef.current) {
        const newTime = percentage * duration;
        playerRef.current.currentTime = newTime;
        setCurrentTime(newTime);
      }
    }
  };
  const handleSkipBack = useCallback(() => {
    const player = playerRef.current;
    if (player) {
      player.currentTime -= SKIP_INTERVAL;
    }
  }, [playerRef]);
  const handleSkipForward = useCallback(() => {
    const player = playerRef.current;
    if (player) {
      player.currentTime += SKIP_INTERVAL;
    }
  }, [playerRef]);

  useEffect(() => {
    const handleMediaKeys = (event: MediaSessionActionDetails) => {
      if (isLive) {
        return;
      }
      if (event.action === "play") {
        togglePlay();
      } else if (event.action === "pause") {
        togglePlay();
      }
    };

    if ("mediaSession" in navigator) {
      navigator.mediaSession.setActionHandler("play", () =>
        handleMediaKeys({ action: "play" }),
      );
      navigator.mediaSession.setActionHandler("pause", () =>
        handleMediaKeys({ action: "pause" }),
      );
    }

    return () => {
      if ("mediaSession" in navigator) {
        navigator.mediaSession.setActionHandler("play", null);
        navigator.mediaSession.setActionHandler("pause", null);
      }
    };
  }, [togglePlay, isLive]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        document.activeElement &&
        (document.activeElement.tagName.toLowerCase() === "input" ||
          document.activeElement.tagName.toLowerCase() === "textarea")
      ) {
        // If an input is focussed ignore player controls
        return;
      }
      // Basic Keyboard media controls
      if (event.code === "Space") {
        event.preventDefault();
        if (!isLive) {
          togglePlay();
        }
      } else if (event.code === "ArrowLeft") {
        event.preventDefault();
        if (!isLive) {
          handleSkipBack();
        }
      } else if (event.code === "ArrowRight") {
        event.preventDefault();
        if (!isLive) {
          handleSkipForward();
        }
      } else if (event.code === "ArrowUp") {
        event.preventDefault();
        handleVolumeIncrease(0.1);
      } else if (event.code === "ArrowDown") {
        event.preventDefault();
        handleVolumeIncrease(-0.1);
      } else if (event.code === "KeyM") {
        event.preventDefault();
        toggleMute();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    togglePlay,
    handleSkipBack,
    handleSkipForward,
    handleVolumeIncrease,
    toggleMute,
    playing,
    isLive,
  ]);

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return {
    togglePlay,
    toggleMute,
    handleVolumeChange,
    handleTimeUpdate,
    handleProgress,
    handleLoadedMetadata,
    handleSeek,
    handleScrub,
    handleSkipBack,
    handleSkipForward,
    volume,
    muted,
    currentTime,
    duration,
    started,
    progressPercentage,
    bufferedPercentage,
  };
};
